import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Card, Col, Form, FormProps, Input, Modal, notification, Row, Select } from 'antd';
import useModal from 'stores/useModal';

import { useMutation } from '@tanstack/react-query';
import { moveTransaction } from 'services/api/module/booking.api';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import {
  FOLIO_BALANCE_CODE,
  ValueSelectedItemsOfTransactionSearchBooking
} from 'services/api/type/booking.type';
import CashierModal from 'components/modal/transaction-modal/transaction-search-booking';
import 'styles/move-transaction.scss';
import { useTranslation } from 'react-i18next';

function MoveTransaction() {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { Search } = Input;

  const { setIsOpenTransactionSearchBooking } = useModal();
  const [selectedItems, setSelectedItems] =
    useState<ValueSelectedItemsOfTransactionSearchBooking>();

  const {
    moveTransaction: { transaction_ids, isOpenMoveTransaction, bookingLineId, code, guest_id },
    closeMoveTransaction
  } = useModal();

  const { data: bookingLineDetailForMoveFrom } = useGetBookingDetail(bookingLineId);

  const { data: bookingLineDetailForMoveTo } = useGetBookingDetail(
    selectedItems?.booking_line_id || bookingLineId
  );

  useEffect(() => {
    const guestId = bookingLineDetailForMoveTo?.booking_line_guests.find(
      item => item.id === guest_id || item.id === selectedItems?.id
    )?.id;

    if (!isOpenMoveTransaction) {
      form.resetFields();
    } else {
      form.setFieldsValue({
        booking_line_id: bookingLineDetailForMoveTo?.booking_line_id,
        guest_id: guestId,
        folio_balance_code: code
      });
    }
    // eslint-disable-next-line
  }, [bookingLineDetailForMoveTo, isOpenMoveTransaction]);

  const { mutateAsync: mutateMoveTransaction, isPending } = useMutation({
    mutationFn: (params: any) =>
      moveTransaction({
        guest_id: params.guest_id,
        transaction_ids: params.transaction_ids,
        folio_balance_code: params.folio_balance_code
      })
  });

  const handleCloseModal = () => {
    closeMoveTransaction();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = async (values: any) => {
    try {
      await mutateMoveTransaction({
        guest_id: values.guest_id,
        transaction_ids: transaction_ids,
        folio_balance_code: values.folio_balance_code
      });
      closeMoveTransaction();
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, guest_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_TRANSACTION_HISTORY_LOGS]
      });
      notification.success({
        message: t('common.message.moveTransaction')
      });
    } catch (err: any) {
      notification.error({
        message: err.error || t('common.error')
      });
    }
  };

  const handleSearch = () => {
    setIsOpenTransactionSearchBooking(true);
  };

  return (
    <Modal
      title={t('cashierPage.moveTransaction.title')}
      centered
      width={450}
      open={isOpenMoveTransaction}
      onCancel={handleCloseModal}
      okText={t('common.actions.save')}
      okButtonProps={{ className: 'ant-btn-secondary' }}
      onOk={() => form.submit()}
      cancelText={t('common.actions.cancel')}
      className={classNames('modal-change-price')}
      confirmLoading={isPending}
      destroyOnClose
    >
      <Form
        form={form}
        layout="horizontal"
        onFinish={onFinish}
        autoComplete="off"
        className="move-transaction-form"
      >
        <Card className="move-transaction-card">
          <h3 className="card-title">{t('cashierPage.common.moveFrom')}</h3>
          <div className="move-transaction-card__body">
            <Row gutter={[8, 0]} className="w-full equal-spacing-row">
              <Col span={24}>
                <Row className="label-span-row" align="middle">
                  <Col span={10} className="label-column">
                    <strong>{t('cashierPage.common.guestName')}</strong>
                  </Col>
                  <Col span={14} className="span-column">
                    <span className="ant-form-text">
                      {bookingLineDetailForMoveFrom?.booking_line_guests
                        .filter(item => item.id === guest_id)
                        .map(item => item.guest_name)}
                    </span>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row className="label-span-row" align="middle">
                  <Col span={10} className="label-column">
                    <strong>{t('cashierPage.common.reservation')}</strong>
                  </Col>
                  <Col span={14} className="span-column">
                    <span className="ant-form-text">
                      {bookingLineDetailForMoveFrom?.booking_line_id}
                    </span>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row className="label-span-row" align="middle">
                  <Col span={10} className="label-column">
                    <strong>{t('cashierPage.moveTransaction.currentPage')}</strong>
                  </Col>
                  <Col span={14} className="span-column">
                    <span className="ant-form-text">{code}</span>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row className="label-span-row" align="middle">
                  <Col span={10} className="label-column">
                    <strong>{t('cashierPage.common.room')}</strong>
                  </Col>
                  <Col span={14} className="span-column">
                    <span className="ant-form-text">
                      {bookingLineDetailForMoveFrom?.room_name ||
                        bookingLineDetailForMoveFrom?.room_type_name}
                    </span>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Card>

        <Card className="move-transaction-card">
          <h3 className="card-title">{t('cashierPage.common.moveTo')}</h3>
          <div className="move-transaction-card__body">
            <Row gutter={[8, 0]} className="w-full equal-spacing-row">
              <Col span={8} style={{ bottom: '8px' }}>
                <p>
                  <span className="required">* </span>
                  {t('cashierPage.common.reservation')}:
                </p>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="booking_line_id"
                  rules={[
                    {
                      required: true,
                      message: t('cashierPage.moveTransaction.reservationRequired')
                    }
                  ]}
                  className="form-item reduced-space label-align-right"
                >
                  <Search
                    placeholder={t('cashierPage.common.reservation')}
                    readOnly
                    onSearch={handleSearch}
                    className="input-size-equal"
                  />
                </Form.Item>
              </Col>
              <Col span={8} style={{ bottom: '8px' }}>
                <p>
                  <span className="required">* </span>
                  {t('cashierPage.common.guestName')}:
                </p>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="guest_id"
                  rules={[
                    { required: true, message: t('cashierPage.moveTransaction.guestNameRequired') }
                  ]}
                  className="form-item reduced-space label-align-right"
                >
                  <Select className="input-size-equal">
                    {bookingLineDetailForMoveTo?.booking_line_guests.map(item => (
                      <Select.Option key={item.id} value={item.id} className="select-option">
                        {item.guest_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8} style={{ bottom: '8px' }}>
                <p>
                  <span className="required">* </span>
                  {t('cashierPage.moveTransaction.folioBalance')}:
                </p>
              </Col>
              <Col span={16}>
                <Form.Item
                  name="folio_balance_code"
                  rules={[
                    {
                      required: true,
                      message: t('cashierPage.moveTransaction.folioBalanceRequired')
                    }
                  ]}
                  className="form-item reduced-space label-align-right"
                >
                  <Select className="input-size-equal">
                    <Select options={FOLIO_BALANCE_CODE}></Select>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Card>
        <CashierModal setSelectedItems={setSelectedItems} />
      </Form>
    </Modal>
  );
}

export default MoveTransaction;
